// ============================================================================
// imageUpload.js — képcsatolás a csevegéshez.
//
// MIÉRT NEM TÖLTJÜK FEL EGYSZERŰEN, AHOGY VAN?
//
// Egy mai telefon fotója 12 megapixel, 4-6 MB. Ezt feltölteni lassú (mobil
// hálózaton akár egy perc), drága (tárolás + forgalom), és a csevegésben
// úgyis legfeljebb 800 pixel szélesen jelenik meg. Ezért a kliensen
// átméretezzük: a végeredmény ~200-400 kB, vizuálisan megkülönböztethetetlen.
//
// BIZTONSÁG: a fájl kiterjesztése és a MIME-típusa egyaránt HAMISÍTHATÓ.
// Egy .jpg-nek álcázott futtatható fájl épp olyan könnyen feltölthető,
// mint egy valódi kép - ha csak a nevét nézzük. Ezért a tényleges bájtokat
// vizsgáljuk (fájlaláírás), és a képet ÚJRA IS RAJZOLJUK egy canvasra:
// ami nem valódi kép, azt a böngésző nem tudja megrajzolni, tehát elbukik.
// ============================================================================

import { log } from "./log.js";

// A csevegésben megjelenő kép maximális mérete. 1600 px elég ahhoz, hogy
// nagy képernyőn és nagyításnál is éles maradjon.
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;
// Ha a tömörített kép mégis túl nagy lenne, ezekkel a minőségekkel
// próbálkozunk tovább. Enélkül egy szokatlanul zajos fotónál a feltöltés
// egyszerűen meghiúsulna - itt viszont MINDIG marad egy működő kimenet.
const FALLBACK_QUALITIES = [0.7, 0.55, 0.4];

// A FELDOLGOZÁS ELŐTTI felső korlát: ennél nagyobb fájlt el sem kezdünk
// beolvasni, mert a böngésző memóriája elfogyhat tőle. 40 MB: egy mai
// 48 megapixeles telefonfotó is belefér, a nyers (RAW) fájlok nem.
const MAX_SOURCE_BYTES = 40 * 1024 * 1024;

// A feldolgozás UTÁNI korlát, biztonsági tartalékként.
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

// A fájlválasztó szűrője. SZÁNDÉKOSAN tág: a szűk, típusonkénti lista
// egyes telefonokon és galéria-alkalmazásokban elrejti a képeket vagy
// megakadályozza a HEIC→JPEG automatikus átalakítást. A tényleges
// ellenőrzést úgyis a fájl bájtjai és a canvas-rajzolás végzi.
// Kép VAGY kis PDF (2026-10-10: számlák) - az elágazást a main.js onImageSelected végzi (pdfCsatolas.js).
export const ACCEPTED_TYPES = "image/*,application/pdf,.pdf";

// Egy bejegyzéshez csatolható képek legnagyobb száma (v211; a szerveren: functions/src/lib/kepek.js MAX_IMAGES_PER_MESSAGE).
export const MAX_KEPEK = 4;

/**
 * A fájl ELSŐ BÁJTJAI alapján dönti el, valóban kép-e.
 * Ezek a "fájlaláírások" (magic bytes) a fájlformátum részei - a
 * kiterjesztéssel vagy a MIME-típussal ellentétben nem lehet őket
 * egyszerűen átírni anélkül, hogy a fájl használhatatlanná válna.
 */
async function isRealImage(file) {
  const fejlec = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  if (fejlec.length < 4) return false;

  // JPEG: FF D8 FF
  if (fejlec[0] === 0xff && fejlec[1] === 0xd8 && fejlec[2] === 0xff) return true;

  // PNG: 89 50 4E 47 0D 0A 1A 0A
  if (
    fejlec[0] === 0x89 && fejlec[1] === 0x50 && fejlec[2] === 0x4e && fejlec[3] === 0x47 &&
    fejlec[4] === 0x0d && fejlec[5] === 0x0a && fejlec[6] === 0x1a && fejlec[7] === 0x0a
  ) return true;

  // GIF: "GIF87a" vagy "GIF89a"
  if (fejlec[0] === 0x47 && fejlec[1] === 0x49 && fejlec[2] === 0x46) return true;

  // WebP: "RIFF" .... "WEBP"
  if (
    fejlec[0] === 0x52 && fejlec[1] === 0x49 && fejlec[2] === 0x46 && fejlec[3] === 0x46 &&
    fejlec[8] === 0x57 && fejlec[9] === 0x45 && fejlec[10] === 0x42 && fejlec[11] === 0x50
  ) return true;

  // HEIC / HEIF (iPhone alapértelmezett formátuma): ISO-BMFF doboz,
  // a 4-7. bájt "ftyp". A telefon a képválasztónál általában JPEG-gé
  // alakítja, de a Fájlok alkalmazásból az eredeti HEIC is kiválasztható.
  // Elfogadjuk; ha a böngésző nem tudja megnyitni (jelenleg a Safarin
  // kívül egyik sem), a resizeImage ad róla ÉRTHETŐ hibaüzenetet.
  if (
    fejlec.length >= 8 &&
    fejlec[4] === 0x66 && fejlec[5] === 0x74 && fejlec[6] === 0x79 && fejlec[7] === 0x70
  ) return true;

  // BMP ("BM") és TIFF ("II*\0" / "MM\0*"): ritka, de a böngészők egy
  // része megnyitja. Ami mégsem, az a canvas-lépésben bukik el.
  if (fejlec[0] === 0x42 && fejlec[1] === 0x4d) return true;
  if (
    (fejlec[0] === 0x49 && fejlec[1] === 0x49 && fejlec[2] === 0x2a && fejlec[3] === 0x00) ||
    (fejlec[0] === 0x4d && fejlec[1] === 0x4d && fejlec[2] === 0x00 && fejlec[3] === 0x2a)
  ) return true;

  return false;
}

/** Igaz, ha a fájl HEIC/HEIF (a hibaüzenet pontosításához). */
async function isHeic(file) {
  try {
    const f = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    return f.length >= 8 && f[4] === 0x66 && f[5] === 0x74 && f[6] === 0x79 && f[7] === 0x70;
  } catch (e) {
    return false;
  }
}

/**
 * A fájl dekódolása rajzolható forrássá.
 *
 * Elsőként a createImageBitmap-ot használjuk `imageOrientation:
 * "from-image"` beállítással: ez a dekódolást a fő szálon kívül végzi
 * (nem akad meg tőle a felület), és KÖVETKEZETESEN alkalmazza a fotó EXIF
 * tájolását. Enélkül a régebbi böngészők a telefonnal, oldalra fordítva
 * készült képet elforgatva töltenék fel.
 *
 * Ahol nincs createImageBitmap (vagy elbukik az adott formátumon), a
 * klasszikus <img> utat használjuk tartalékként.
 */
async function decodeImage(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (e) {
      // Néhány böngésző nem ismeri a második paramétert - próbáljuk nélküle.
      try {
        return await createImageBitmap(file);
      } catch (e2) {
        // Marad az <img> tartalék.
      }
    }
  }
  return loadImage(file);
}

/** A fájl betöltése képként. Ami nem valódi kép, itt elbukik. */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("A fájl nem nyitható meg képként."));
    };
    img.src = url;
  });
}

/**
 * Átméretezés és tömörítés.
 *
 * A canvasra újrarajzolás mellékhatásként MEGTISZTÍTJA a képet: az EXIF
 * metaadatok (GPS-koordináta, eszköz típusa, készítés ideje) NEM kerülnek
 * át. Ez adatvédelmi szempontból fontos - egy telefonnal készült fotó
 * alapértelmezésben tartalmazza a készítés helyét.
 */
async function resizeImage(file) {
  const img = await decodeImage(file);

  // A natural* a kép TÉNYLEGES pixelmérete; a width/height <img>-nél
  // elvileg ugyanez, de attribútummal felülírható - ne arra hagyatkozzunk.
  let width = img.naturalWidth || img.width;
  let height = img.naturalHeight || img.height;
  if (!width || !height) {
    if (typeof img.close === "function") img.close();
    throw new Error("A kép mérete érvénytelen.");
  }

  // Arányos kicsinyítés, ha bármelyik oldal túl nagy.
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const arany = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    width = Math.max(1, Math.round(width * arany));
    height = Math.max(1, Math.round(height * arany));
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  // Jobb minőségű átméretezés (alapból a böngésző gyorsabb, de durvább
  // algoritmust használna).
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Fehér háttér: az átlátszó PNG-k JPEG-be mentve különben feketék
  // lennének, ami csúnya meglepetés.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  // A dekódolt bitkép memóriájának azonnali elengedése - egy 48 megapixeles
  // fotónál ez több száz megabájt.
  if (typeof img.close === "function") img.close();

  // Az első próbálkozás után, ha a méret mégis a korlát fölött maradna,
  // csökkenő minőséggel próbálkozunk. Így nincs olyan valódi fotó, amit
  // a felhasználó ne tudna elküldeni.
  let blob;
  try {
    blob = await toJpeg(canvas, JPEG_QUALITY);
    for (const q of FALLBACK_QUALITIES) {
      if (blob && blob.size <= MAX_UPLOAD_BYTES) break;
      blob = await toJpeg(canvas, q);
    }
  } finally {
    // A vászon háttértárának AZONNALI elengedése (audit, 2026-10-15): iOS Safarin a vászonmemória összkerete véges, és
    // a szemétgyűjtésig nem szabadul fel - több nagy fotó egymás utáni feltöltése után a rajzolás csendben elbukhatna.
    canvas.width = 0;
    canvas.height = 0;
  }

  if (!blob) throw new Error("A kép tömörítése nem sikerült.");
  if (blob.size > MAX_UPLOAD_BYTES) throw new Error("A kép a tömörítés után is túl nagy.");

  return { blob, width, height };
}

/** canvas → JPEG blob, Promise-ként. */
function toJpeg(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

/**
 * A kiválasztott fájl teljes előkészítése feltöltéshez.
 * Visszaad: { blob, width, height, previewUrl } vagy hibát dob.
 */
export async function prepareImage(file) {
  if (!file) throw new Error("Nincs kiválasztott fájl.");

  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error("A kép túl nagy (legfeljebb 40 MB).");
  }

  // 1. réteg: a bejelentett típus
  if (!file.type || !file.type.startsWith("image/")) {
    throw new Error("Csak képet lehet csatolni.");
  }

  // 2. réteg: a tényleges bájtok
  if (!(await isRealImage(file))) {
    throw new Error("Ez a fájl nem valódi kép.");
  }

  // 3. réteg: a böngésző meg tudja-e nyitni képként (a méretkorlátot a
  // resizeImage már betartatja, csökkenő minőséggel újrapróbálva)
  let blob, width, height;
  try {
    ({ blob, width, height } = await resizeImage(file));
  } catch (err) {
    if (await isHeic(file)) {
      throw new Error(
        "Ez a böngésző nem tudja megnyitni a HEIC képeket. A telefon " +
        "beállításaiban válaszd a „Leginkább kompatibilis” (JPEG) fotóformátumot, " +
        "vagy küldd a képet a galériából megosztva."
      );
    }
    throw err;
  }

  return {
    blob,
    width,
    height,
    previewUrl: URL.createObjectURL(blob),
    originalSize: file.size,
    compressedSize: blob.size,
    // Az EREDETI fájl hivatkozása: a "@2" kódú küldésnél a képet a tömörítés
    // nélküli eredetiből kell feltölteni (lásd prepareOriginalImage). Csak
    // hivatkozás - a böngésző nem másolja le a tartalmát.
    eredetiFajl: file,
  };
}

// ---------------------------------------------------------------------------
// "@2": az EREDETI, tömörítetlen kép (2026-10-06, tagi kérésre)
//
// A "@2" kódú képet a szerver a Gemini-vel olvastatja ki (functions/src/
// imageEnhance.js). Apró, sűrű nyomtatott szövegnél (régi újságoldal) a
// 1600 px-re kicsinyített, újratömörített JPEG már elveszti a vékony vonásokat -
// ezért ennél a kódnál a fájl VÁLTOZTATÁS NÉLKÜL töltődik fel (a File bájtjai
// érintetlenül mennek a Storage-ba, nincs átméretezés, nincs újrakódolás).
//
// KORLÁTOK (a Storage-szabály és a Gemini kéréskorlátja miatt):
//  - legfeljebb ORIGINAL_MAX_BYTES (10 MB): a Storage-szabály 10 MB-ig enged, és
//    a Gemini beágyazott kérése (base64-gel ~1,33x) 20 MB alatt marad;
//  - legfeljebb 10 000 képpont oldalanként (a szerver ennyit fogad el);
//  - JPEG, PNG vagy WebP: a Gemini ezeket olvassa (a GIF-et és a HEIC-et nem).
//    Más formátumnál NINCS eredeti-út - a hívó a szokásos (tömörített) képet
//    használja, ami HEIC-nél amúgy is hibaüzenetet ad.
//
// ADATVÉDELMI KÖVETKEZMÉNY: a szokásos feltöltés a canvas-újrarajzolás révén
// megtisztítja a képet az EXIF-adatoktól (GPS-hely, eszköz, idő). Az eredeti
// fájlban ezek BENNE MARADNAK, és a szoba tagjai letölthetik.
// ---------------------------------------------------------------------------

export const ORIGINAL_MAX_BYTES = 10 * 1024 * 1024;
export const ORIGINAL_MAX_DIMENSION = 10000;
const ORIGINAL_KITERJESZTES = new Map([["image/jpeg", "jpg"], ["image/png", "png"], ["image/webp", "webp"]]);

/** Mikor kell az eredeti kép? PONTOSAN ugyanaz a szabály, mint a szerveren (aiParancs): a szöveg a "@2" (szóközök levágva). */
export function eredetiKellEz(szoveg) {
  return typeof szoveg === "string" && szoveg.trim() === "@2";
}

/** A fájl TÉNYLEGES típusa az első bájtjai alapján (a bejelentett típus hamisítható). */
export function kepTipusBajtokbol(fejlec) {
  if (!fejlec || fejlec.length < 12) return null;
  if (fejlec[0] === 0xff && fejlec[1] === 0xd8 && fejlec[2] === 0xff) return "image/jpeg";
  if (fejlec[0] === 0x89 && fejlec[1] === 0x50 && fejlec[2] === 0x4e && fejlec[3] === 0x47) return "image/png";
  if (fejlec[0] === 0x47 && fejlec[1] === 0x49 && fejlec[2] === 0x46 && fejlec[3] === 0x38) return "image/gif";
  if (
    fejlec[0] === 0x52 && fejlec[1] === 0x49 && fejlec[2] === 0x46 && fejlec[3] === 0x46 &&
    fejlec[8] === 0x57 && fejlec[9] === 0x45 && fejlec[10] === 0x42 && fejlec[11] === 0x50
  ) return "image/webp";
  return null;
}

async function olvasdKiAKepMeretet(file) {
  const img = await decodeImage(file);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (typeof img.close === "function") img.close();
  return { width, height };
}

function eredetiHiba(kod, uzenet) {
  const e = new Error(uzenet);
  e.code = kod;
  return e;
}

/**
 * Az EREDETI fájl előkészítése feltöltéshez: semmi nem változik rajta, csak
 * ellenőrizzük és kiolvassuk a méretét.
 *
 * @returns {Promise<object|null>} { blob: <az eredeti File>, width, height, previewUrl, eredeti: true,
 *   contentType, kiterjesztes, ... }, vagy null, ha ennél a formátumnál nincs eredeti-út (a hívó a
 *   tömörített képet használja). Túl nagy fájlnál/képnél "app/original-too-large" hibát dob.
 */
export async function prepareOriginalImage(file, { meretOlvaso = olvasdKiAKepMeretet } = {}) {
  if (!file) throw new Error("Nincs kiválasztott fájl.");
  const fejlec = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const tipus = kepTipusBajtokbol(fejlec);
  if (!tipus || !ORIGINAL_KITERJESZTES.has(tipus)) return null;

  if (file.size > ORIGINAL_MAX_BYTES) {
    throw eredetiHiba("app/original-too-large", "Az eredeti kép túl nagy.");
  }
  const { width, height } = await meretOlvaso(file);
  if (!(width > 0) || !(height > 0)) throw new Error("A kép mérete érvénytelen.");
  if (width > ORIGINAL_MAX_DIMENSION || height > ORIGINAL_MAX_DIMENSION) {
    throw eredetiHiba("app/original-too-large", "Az eredeti kép mérete túl nagy.");
  }
  return {
    blob: file, // a File maga: a bájtok érintetlenül mennek a Storage-ba
    width,
    height,
    previewUrl: URL.createObjectURL(file),
    originalSize: file.size,
    compressedSize: file.size,
    eredeti: true,
    contentType: tipus,
    kiterjesztes: ORIGINAL_KITERJESZTES.get(tipus),
    eredetiFajl: file,
  };
}

// A kép AKTUÁLIS változatának kulcsai: ezeket cseréli a kepValtozat.
const VALTOZAT_KULCSOK = ["blob", "width", "height", "previewUrl", "originalSize", "compressedSize"];

/**
 * A függőben lévő kép változatának igazítása ahhoz, hogy a "@2" kell-e
 * (2026-10-06): "@2"-nél az eredeti, tömörítetlen fájl, minden másnál a
 * kicsinyített. A kicsinyített változat megmarad (`kep.tomoritett`), így a
 * szöveg visszaírásakor nincs újra tömörítés, és az előnézet is rögtön az
 * éppen feltöltendő fájl méretét mutatja - nem csak küldéskor derül ki.
 *
 * A KORÁBBAN feltöltött változat (kep.uploaded) érvényét veszti, ha a változat
 * cserélődik: egy másik fájlnak másik feltöltés kell.
 *
 * @param {object} kep a függőben lévő kép (prepareImage eredménye, helyben módosul)
 * @param {boolean} kellEredeti
 * @param {{elokeszito?: Function}} [opciok] tesztekhez
 * @returns {Promise<"eredeti"|"tomoritett"|null>} melyik változatra váltott, vagy null, ha nem kellett váltani
 */
export async function kepValtozat(kep, kellEredeti, { elokeszito = prepareOriginalImage } = {}) {
  if (!kep) return null;
  if (kellEredeti && !kep.eredeti && kep.eredetiFajl) {
    const eredeti = await elokeszito(kep.eredetiFajl); // túl nagynál hibát dob (app/original-too-large)
    if (!eredeti) return null; // ennél a formátumnál nincs eredeti-út (pl. GIF)
    kep.tomoritett = Object.fromEntries(VALTOZAT_KULCSOK.map((k) => [k, kep[k]]));
    Object.assign(kep, eredeti);
    kep.uploaded = null;
    return "eredeti";
  }
  if (!kellEredeti && kep.eredeti && kep.tomoritett) {
    releasePreview(kep.previewUrl); // az eredeti előnézete már nem kell
    Object.assign(kep, kep.tomoritett);
    kep.tomoritett = null;
    kep.eredeti = false;
    delete kep.contentType;
    delete kep.kiterjesztes;
    kep.uploaded = null;
    return "tomoritett";
  }
  return null;
}

/** A kép MINDEN előnézeti URL-jének elengedése (az aktuálisé és a félretett kicsinyítetté is). */
export function releaseImage(kep) {
  if (!kep) return;
  releasePreview(kep.previewUrl);
  if (kep.tomoritett) releasePreview(kep.tomoritett.previewUrl);
  // A további képek (v211: kep.extras) előnézete is.
  if (Array.isArray(kep.extras)) for (const tovabbi of kep.extras) releasePreview(tovabbi && tovabbi.previewUrl);
}

/** A felszabadítandó előnézeti URL elengedése (memóriaszivárgás ellen). */
export function releasePreview(previewUrl) {
  if (previewUrl) {
    try {
      URL.revokeObjectURL(previewUrl);
    } catch (e) {
      // nem kritikus
    }
  }
}

// --- Bélyegkép (v222, #67): a listában kis változat jelenik meg (~14x kisebb forgalom), a teljes kép koppintásra töltődik.
export const BELYEG_MAX = 480;
// Ennél kisebb képnek nincs értelme bélyeget készíteni (a fő kép is alig nagyobb).
const BELYEG_MIN_FORRAS = 640;
const BELYEG_MINOSEG = 0.7;

/** A bélyeg méretei (a hosszabbik oldal legfeljebb `max`), vagy null, ha a kép eleve kicsi. Tiszta függvény (tesztelhető). */
export function belyegMeret(width, height, max = BELYEG_MAX, minForras = BELYEG_MIN_FORRAS) {
  if (!(width > 0) || !(height > 0)) return null;
  if (Math.max(width, height) <= minForras) return null;
  const arany = Math.min(max / width, max / height, 1);
  return { width: Math.max(1, Math.round(width * arany)), height: Math.max(1, Math.round(height * arany)) };
}

/**
 * A (már kicsinyített) JPEG-ből bélyegképet készít. SOHA nem dob hibát: ha bármi nem sikerül, null (a kép bélyeg nélkül is rendben megy).
 * @returns {Promise<{blob: Blob, width: number, height: number}|null>}
 */
export async function keszitBelyeget(blob, width, height) {
  try {
    const meret = belyegMeret(width, height);
    if (!meret || typeof document === "undefined" || typeof createImageBitmap !== "function") return null;
    const bitkep = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = meret.width;
    canvas.height = meret.height;
    try {
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, meret.width, meret.height);
      ctx.drawImage(bitkep, 0, 0, meret.width, meret.height);
      const kicsi = await toJpeg(canvas, BELYEG_MINOSEG);
      // Ha a bélyeg nem kisebb a fő képnél, nincs értelme.
      if (!kicsi || kicsi.size >= blob.size) return null;
      return { blob: kicsi, width: meret.width, height: meret.height };
    } finally {
      if (typeof bitkep.close === "function") bitkep.close();
      canvas.width = 0;
      canvas.height = 0;
    }
  } catch (e) {
    return null;
  }
}

/**
 * A kép feltöltése bélyeggel együtt. A bélyeg hibája NEM akadályozza a küldést (csak bélyeg nélkül megy).
 * @returns {Promise<{path: string, url: string, thumbPath?: string, thumbUrl?: string}>}
 */
export async function uploadImageWithThumb({ blob, width, height, roomId, uid, onProgress }) {
  const fo = await uploadImage({ blob, roomId, uid, onProgress });
  try {
    const kicsi = await keszitBelyeget(blob, width, height);
    if (!kicsi) return fo;
    const b = await uploadImage({ blob: kicsi.blob, roomId, uid });
    return { ...fo, thumbPath: b.path, thumbUrl: b.url };
  } catch (e) {
    log.warn && log.warn("[kép] a bélyegkép feltöltése nem sikerült, bélyeg nélkül küldjük:", e && e.code);
    return fo;
  }
}

/**
 * A kép feltöltése a Storage-ba.
 *
 * Az útvonal tartalmazza a UID-t: a Storage-szabályok ebből ellenőrzik,
 * hogy mindenki csak a saját nevében tölt fel.
 *
 * @param {function} onProgress Opcionális: 0-100 közötti százalék.
 */
export async function uploadImage({ blob, roomId, uid, onProgress, contentType = "image/jpeg", kiterjesztes = "jpg" }) {
  const { getStorage, ref, uploadBytesResumable, getDownloadURL } = await import(
    "firebase/storage"
  );
  const { getFirebaseApp } = await import("./firebaseClient.js");

  const storage = getStorage(getFirebaseApp());

  // Egyedi fájlnév: időbélyeg + véletlen rész. Így két egyszerre
  // feltöltött kép sem írja felül egymást.
  const veletlen = crypto.randomUUID ? crypto.randomUUID().slice(0, 8) : Math.random().toString(36).slice(2, 10);
  const fileName = `${Date.now()}-${veletlen}.${kiterjesztes}`;
  const path = `rooms/${roomId}/${uid}/${fileName}`;

  const storageRef = ref(storage, path);
  const task = uploadBytesResumable(storageRef, blob, {
    // A normál úton mindig JPEG; az eredeti-útnál (prepareOriginalImage) a fájl
    // TÉNYLEGES típusa - a Storage-szabály csak jpeg|png|webp|gif-et enged.
    contentType,
    cacheControl: "public, max-age=31536000, immutable",
  });

  return new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snapshot) => {
        if (onProgress && snapshot.totalBytes > 0) {
          onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
        }
      },
      (err) => {
        log.error("[kép] feltöltés hiba:", err);
        reject(err);
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve({ url, path });
        } catch (err) {
          reject(err);
        }
      }
    );
  });
}
